/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import renderMathInElement from 'katex/contrib/auto-render';
import 'katex/contrib/mhchem';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import FocusLock from 'react-focus-lock';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkBreaks from 'remark-breaks';
import remarkMath from 'remark-math';
import { preprocessNumberLineContent } from 'src/app/lib/numberline-processor';
import { visit } from 'unist-util-visit';
import rehypeKatex from '../../../patches/rehype-katex';
import remarkGfmNoAutolink from '../../../patches/remark-gfm';
import styles from './LatexHTMLRenderer.module.scss';
import {
  addMathScreenReaderText,
  calculatePopupPosition,
  cleanupEquationElements,
  EquationPopup,
  extractEquationData,
  isEquationLong,
  openEquationAsImage,
  showMessage,
  toggleEquationDisplayMode,
} from './utilities';

import { t } from 'i18next';
import { preprocessListJson } from 'src/app/lib/list-preprocessor';
import { preprocessTimelineContent } from 'src/app/lib/timeline-preprocessor';
import { preprocessXYCursorContent } from 'src/app/lib/xycursor-preprocessor';
import { preprocessXYGraphContent } from 'src/app/lib/xygraph';
import { replaceLHTWithSrOnlyHTML } from 'src/app/lib/sr-lht-preprocessor';
import XYCursor from '../XYCursor';

type LatexHTMLRendererPropTypes = {
  content: string;
  className?: string;
  size?: 'medium' | 'small';
  onRenderComplete?: () => void;
};

// Custom rehype plugin to remove <p> tags around KaTeX
function removePAroundKatex() {
  return (tree: any) => {
    visit(tree, 'paragraph', (node) => {
      if (node.children.length === 1 && node.children[0].type === 'math') {
        node.replaceWith(node.children[0]); // Replace <p> with <math>
      }
      console.log('_onLog - tree', node);
    });
  };
}

/**
 * Component to parse Latex and HTML Markup
 *
 * This component first processes the content to fix formatting issues with LaTeX,
 * then processes with rehypeKatex for markdown LaTeX, and finally applies
 * auto-render for any LaTeX in HTML contexts.
 */
function LatexHTMLRenderer(props: LatexHTMLRendererPropTypes) {
  const { content, className = 'text-main', size = 'medium' } = props;
  const markdownRef = useRef<HTMLDivElement>(null);
  const altMarkdownRef = useRef<HTMLParagraphElement | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const mouseOverElementRef = useRef<boolean>(false);
  const mouseOverPopupRef = useRef<boolean>(false);
  const hidePopupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const showPopupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentHoveredElement = useRef<Element | null>(null);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const [modalImageSrc, setModalImageSrc] = useState<string | null>(null);
  const [modalImageAlt, setModalImageAlt] = useState<string | null>(null);
  const scrollbarCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [popup, setPopup] = useState<EquationPopup>({
    isVisible: false,
    position: { x: 0, y: 0 },
    htmlContent: '',
    targetElement: null,
    isDisplayMode: false,
    isLongEquation: false,
  });

  const returnParsedContent = useCallback((content: string | null) => {
    if (!content) return '';

    let parsedContent = String(content);

    // Process embedded list JSON
    parsedContent = preprocessListJson(parsedContent);
    parsedContent = preprocessXYGraphContent(parsedContent);
    parsedContent = preprocessNumberLineContent(parsedContent);
    parsedContent = preprocessTimelineContent(parsedContent);
    parsedContent = preprocessXYCursorContent(parsedContent);
    parsedContent = replaceLHTWithSrOnlyHTML(parsedContent);

    // Fix inconsistent spacing issues that break KaTeX rendering in edge cases
    parsedContent = parsedContent.replace(/\$\$([\s\S]*?)\$\$/g, (match, content) => {
      // replace \r and \n if it is in between latex delimiters
      const modifiedContent = content.replace(/\r?\n/g, ' ');
      return `$$${modifiedContent}$$`;
    });

    return parsedContent;
  }, []);

  function autoWrapLatexInMarkdown(input: string): string {
    if (!input) return '';

    // Avoid wrapping if already wrapped
    const inlineMathPattern = /(^|[^\$\\])\\[a-zA-Z]+(?:\{[^}]+\})*(?![\}\$])/g;

    // Wrap things like \frac{a}{b}, \rho etc. that aren't already in $...$
    const wrapped = input.replace(inlineMathPattern, (match, prefix) => {
      return `${prefix}$${match.trim()}$`;
    });

    return wrapped;
  }

  const parseContent = useMemo(() => returnParsedContent(content), [content, returnParsedContent]);
  const parseExpandedImageAltContent = useMemo(() => {
    const wrapped = autoWrapLatexInMarkdown(modalImageAlt ?? '');
    return returnParsedContent(wrapped);
  }, [modalImageAlt, returnParsedContent]);

  const clearTimeouts = () => {
    if (hidePopupTimeoutRef.current) {
      clearTimeout(hidePopupTimeoutRef.current);
      hidePopupTimeoutRef.current = null;
    }
    if (showPopupTimeoutRef.current) {
      clearTimeout(showPopupTimeoutRef.current);
      showPopupTimeoutRef.current = null;
    }
  };

  const showPopup = (event: MouseEvent, element: Element) => {
    clearTimeouts();

    // Check if this is a long equation
    const isLong = markdownRef.current ? isEquationLong(element, markdownRef.current) : false;

    // Only show popup for long equations
    if (!isLong) {
      return;
    }

    // Remove highlight from previous element if different
    if (popup.targetElement && popup.targetElement !== element) {
      /* empty */
    }

    currentHoveredElement.current = element;
    mouseOverElementRef.current = true;

    const { htmlContent, isDisplayMode } = extractEquationData(element);

    // Show popup with 1s delay for long equations
    showPopupTimeoutRef.current = setTimeout(() => {
      // Double-check that we're still hovering the same element
      if (currentHoveredElement.current === element && mouseOverElementRef.current) {
        const rect = element.getBoundingClientRect();
        const popupWidth = popupRef.current ? popupRef.current.offsetWidth : 200;
        const popupHeight = popupRef.current ? popupRef.current.offsetHeight : 100;
        const position = calculatePopupPosition(rect, popupWidth, popupHeight);

        setPopup({
          isVisible: true,
          position,
          htmlContent: htmlContent,
          targetElement: element,
          isDisplayMode: isDisplayMode,
          isLongEquation: true,
        });
      }
    }, 500); // 0.5 seconds delay to prevent glitchy effect when user moves across various formuals
  };

  const hidePopup = (immediate = false) => {
    clearTimeouts();

    if (immediate) {
      currentHoveredElement.current = null;
      setPopup((prev) => ({ ...prev, isVisible: false }));
    } else {
      hidePopupTimeoutRef.current = setTimeout(() => {
        if (!mouseOverElementRef.current && !mouseOverPopupRef.current) {
          currentHoveredElement.current = null;
          setPopup((prev) => ({ ...prev, isVisible: false }));
        }
      }, 200);
    }
  };

  const handleElementMouseLeave = (element: Element) => {
    mouseOverElementRef.current = false;
    currentHoveredElement.current = null;

    // Clear the delayed popup if we haven't shown it yet
    clearTimeouts();
    hidePopup();
  };

  const handleToggleDisplayMode = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();

    if (!popup.targetElement) return;

    const newDisplayMode = toggleEquationDisplayMode(popup.targetElement);

    setPopup((prev) => ({
      ...prev,
      isDisplayMode: newDisplayMode,
    }));
  };

  const setupMathElementHandlers = () => {
    if (!markdownRef.current) return;

    const mathElements = markdownRef?.current?.querySelectorAll('.katex');

    mathElements.forEach((element: any) => {
      // Remove existing event listeners and classes first
      const newElement = element.cloneNode(true) as Element;
      if (element.parentNode) {
        element.parentNode.replaceChild(newElement, element);
      }

      // Recalculate if this equation is long based on current zoom/dimensions
      const isLong = markdownRef.current ? isEquationLong(newElement, markdownRef.current) : false;

      // Remove old class and add new one based on current calculation
      newElement.classList.remove('long-equation');

      if (isLong) {
        newElement.classList.add('long-equation');

        newElement.addEventListener('mouseenter', (e) => {
          showPopup(e as MouseEvent, newElement);
        });

        newElement.addEventListener('mouseleave', () => {
          handleElementMouseLeave(newElement);
        });
      }
    });
  };

  // KaTeX always renders numerals/letters with its serif math font, even for
  // plain values like "4" that carry no real math meaning (fractions,
  // exponents, operators, \text runs). Flag those as plain text so CSS can
  // switch them back to the app's body font instead of looking like Times
  // New Roman next to the surrounding Inter text.
  const tagPlainTextKatex = () => {
    if (!markdownRef.current) return;

    const katexElements = markdownRef.current.querySelectorAll('.katex');

    katexElements.forEach((katexEl) => {
      const isPlainText = !katexEl.querySelector(
        '.mfrac, .msupsub, .msub, .msup, .sqrt, .mathnormal, .mop, .mbin, .mrel, .mpunct'
      );

      katexEl.classList.toggle(styles.katexPlainText, isPlainText);
    });
  };

  const handlePopupMouseEnter = () => {
    mouseOverPopupRef.current = true;
    clearTimeouts();
  };

  const handlePopupMouseLeave = () => {
    mouseOverPopupRef.current = false;
    hidePopup();
  };

  const handleOpenAsImage = async (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (!popup.htmlContent) return;

    hidePopup(true);

    await openEquationAsImage(popup.htmlContent, styles.equationModal, (error) =>
      showMessage(error, styles.copyMessage)
    );
  };

  const handleImageClick = (
    e: React.MouseEvent<HTMLImageElement, MouseEvent>,
    src: string,
    alt: string
  ) => {
    e.stopPropagation();
    setModalImageSrc(src);
    setModalImageAlt(alt);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLImageElement>, _src: string, alt: string) => {
    if (e.key === 'Tab') return;

    e.stopPropagation();
    e.preventDefault();
    if (e.key === 'Enter') {
      setModalImageSrc(_src);
      setModalImageAlt(alt);
    }
  };

  const handleModalClose = () => {
    setModalImageSrc(null);
    setModalImageAlt(null);
  };

  /**
   * The function template can be used for preprocessor functions
   * @returns static parsed content
   */
  const getParsedContent = useCallback(() => {
    return parseContent;
  }, [parseContent]);

  useEffect(() => {
    if (markdownRef.current) {
      renderMathInElement(markdownRef.current, {
        delimiters: [
          { left: '$$', right: '$$', display: false }, // By default, set display to false (inline mode)
        ],
        // MathML carries the TeX annotation the screen-reader text is built from
        output: 'htmlAndMathml',
        throwOnError: false,
      });
      addMathScreenReaderText(markdownRef.current);

      setTimeout(() => {
        // prevent screen readers from reading this hidden character
        document.querySelectorAll('.mord.rlap').forEach((el) => {
          el.setAttribute('aria-hidden', 'true');
        });
        setupMathElementHandlers();
        tagPlainTextKatex();
      }, 100);
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        hidePopup(true);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      clearTimeouts();
      hidePopup(true);
    };
  }, [parseContent]);

  // Hydrate XYCursor placeholder divs with React components
  useEffect(() => {
    if (!markdownRef.current) return;

    const xyCursorContainers = markdownRef.current.querySelectorAll('.xycursor-container');

    xyCursorContainers.forEach((container) => {
      const dataAttr = container.getAttribute('data-xycursor');
      if (!dataAttr) return;

      try {
        const xyCursorData = JSON.parse(dataAttr.replace(/&apos;/g, "'"));

        // Try this instead of createRoot
        ReactDOM.render(<XYCursor data={xyCursorData} />, container);
      } catch (error) {
        console.error('Error parsing XYCursor data:', error);
        container.innerHTML = '<div class="xycursor-error">Error loading interactive graph</div>';
      }
    });

    // Cleanup function
    return () => {
      xyCursorContainers.forEach((container) => {
        const root = (container as any)._reactRoot;
        if (root) {
          root.unmount();
          delete (container as any)._reactRoot;
        }
      });
    };
  }, [parseContent]);

  // Show scroll only when scroll width & content width has diff greater than 1px
  useEffect(() => {
    const checkHorizontalScroll = () => {
      if (!markdownRef.current) return;

      const container = markdownRef.current;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;
      const difference = scrollWidth - clientWidth;

      // Hide horizontal scrollbar if difference is <= 1px
      if (difference <= 1 && difference >= 0) {
        container.style.overflowX = 'hidden';
      } else {
        // Restore original overflow-x (from CSS: 'auto')
        container.style.overflowX = 'auto';
      }
    };

    const scheduleCheck = (delay = 0) => {
      if (scrollbarCheckTimeoutRef.current) {
        clearTimeout(scrollbarCheckTimeoutRef.current);
      }
      scrollbarCheckTimeoutRef.current = setTimeout(() => {
        checkHorizontalScroll();
        props.onRenderComplete?.();
      }, delay);
    };

    scheduleCheck(200);

    const container = markdownRef.current;
    const images = container?.querySelectorAll('img') ?? [];
    const onImageSettled = () => scheduleCheck(0);

    images.forEach((img) => {
      if (img.complete) return;
      img.addEventListener('load', onImageSettled);
      img.addEventListener('error', onImageSettled);
    });

    const handleResize = () => scheduleCheck(150);
    window.addEventListener('resize', handleResize);

    return () => {
      if (scrollbarCheckTimeoutRef.current) {
        clearTimeout(scrollbarCheckTimeoutRef.current);
      }
      window.removeEventListener('resize', handleResize);
      images.forEach((img) => {
        img.removeEventListener('load', onImageSettled);
        img.removeEventListener('error', onImageSettled);
      });

      // Restore original overflow on cleanup
      if (markdownRef.current) {
        markdownRef.current.style.overflowX = '';
      }
    };
  }, [parseContent]);

  useEffect(() => {
    const handleInlinePopupClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      if (target?.classList?.contains('inline-popup')) {
        e.preventDefault();

        const popupId = target.getAttribute('data-popup-id');
        const popupContentElement = popupId ? document.getElementById(popupId) : null;

        if (popupContentElement) {
          setPopup({
            isVisible: true,
            position: { x: e.clientX, y: e.clientY },
            htmlContent: popupContentElement.innerHTML,
            targetElement: target,
            isDisplayMode: false,
            isLongEquation: false, // This is list popup, not an equation
          });
        }
      }
    };

    document.addEventListener('click', handleInlinePopupClick);
    return () => {
      document.removeEventListener('click', handleInlinePopupClick);
    };
  }, []);

  // Global mouse tracking and scroll handling
  useEffect(() => {
    const handleDocumentMouseLeave = () => {
      mouseOverElementRef.current = false;
      mouseOverPopupRef.current = false;
      clearTimeouts();
      hidePopup(true);
    };

    const handleScroll = () => {
      clearTimeouts();
      hidePopup(true);
    };

    const handleResize = () => {
      // Debounce to avoid excessive recalculations
      clearTimeout(resizeTimeoutRef.current);
      resizeTimeoutRef.current = setTimeout(() => {
        setupMathElementHandlers();
      }, 150);
    };

    window.addEventListener('resize', handleResize);

    document.addEventListener('mouseleave', handleDocumentMouseLeave);
    document.addEventListener('scroll', handleScroll, true); // Use capture to catch all scroll events
    window.addEventListener('scroll', handleScroll);

    return () => {
      document.removeEventListener('mouseleave', handleDocumentMouseLeave);
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);

      // Cleanup function to remove any remaining elements
      cleanupEquationElements();

      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (modalImageSrc) {
      document.body.style.overflow = 'hidden';
      const closeBtn = document.querySelector<HTMLButtonElement>(`.${styles.imageModalClose}`);
      closeBtn?.focus();
    } else {
      document.body.style.overflow = '';
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && modalImageSrc) handleModalClose();
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [modalImageSrc]);

  const ImageComponent = useCallback(({ src, alt, ...props }: any) => {
    let _src = src;

    if (!src) {
      return null;
    }
    if (props?.about === 'image/svg') {
      if (src !== undefined) {
        _src = `data:image/svg+xml;base64,${src}`;
        props.style = {
          ...props.style,
          width: 'auto',
          height: '300px',
          margin: '10px auto',
        };
      }
    }

    return (
      <img
        src={_src}
        alt={alt}
        tabIndex={0}
        onClick={(e) => handleImageClick(e, src ?? '', alt ?? '')}
        onKeyDown={(e) => handleKeyDown(e, _src ?? '', alt ?? '')}
        aria-label={alt}
        style={{
          cursor: 'zoom-in',
          maxWidth: 'unset',
          width: 'auto',
          height: '100%',
          maxHeight: '300px',
          margin: '0 auto',
        }}
        {...props}
      />
    );
  }, []);

  const markdownComponents = useMemo(
    () => ({
      img: ImageComponent,
    }),
    [ImageComponent]
  );

  const popupStyle = {
    position: 'fixed',
    top: `${popup.position.y}px`,
    left: `${popup.position.x}px`,
    zIndex: 1000,
  } as React.CSSProperties;

  return (
    <>
      <div
        className={`${styles.latexHtmlRenderer} ${size === 'small' ? styles.bodySmall : styles.bodyMedium} ${className}`}
        ref={markdownRef}
      >
        <ReactMarkdown
          // eslint-disable-next-line react/no-children-prop
          children={getParsedContent()}
          remarkPlugins={[remarkMath, remarkGfmNoAutolink, remarkBreaks]}
          rehypePlugins={[rehypeKatex, rehypeRaw, removePAroundKatex]}
          components={markdownComponents}
        />
      </div>

      {popup.isVisible && popup.isLongEquation && (
        <div
          className={styles.equationPopup}
          style={popupStyle}
          ref={popupRef}
          onMouseEnter={handlePopupMouseEnter}
          onMouseLeave={handlePopupMouseLeave}
        >
          <button
            onClick={handleOpenAsImage}
            className={styles.popupButton}
            type='button'
            aria-label='Open formula as image'
          >
            {t(`OPEN_IMAGE`)}
          </button>
          <button
            onClick={handleToggleDisplayMode}
            className={styles.popupButton}
            type='button'
            aria-label={'Display formula as ' + popup.isDisplayMode ? t('INLINE') : t('BLOCK')}
          >
            {t(`DISPLAY_AS`)} {popup.isDisplayMode ? t('INLINE') : t('BLOCK')}
          </button>
        </div>
      )}

      {modalImageSrc &&
        ReactDOM.createPortal(
          <div
            aria-label={t(`FULL_SCREEN_IMAGE_VIEWER`)}
            role='dialog'
            aria-modal='true'
            className={styles.imageModalOverlay}
            onClick={handleModalClose}
          >
            <FocusLock>
              <div className={styles.imageModalContent} onClick={(e) => e.stopPropagation()}>
                <img
                  aria-label={modalImageAlt ?? ''}
                  tabIndex={0}
                  src={modalImageSrc}
                  alt={modalImageAlt ?? ''}
                  className={styles.imageModalImage}
                />
                <button
                  aria-label={t(`CLOSE_FULL_SCREEN_IMAGE`)}
                  className={styles.imageModalClose}
                  onClick={handleModalClose}
                >
                  &times;
                </button>
                {parseExpandedImageAltContent && (
                  <p className={styles.modalImageAlt} ref={altMarkdownRef}>
                    <ReactMarkdown
                      // eslint-disable-next-line react/no-children-prop
                      children={parseExpandedImageAltContent}
                      remarkPlugins={[remarkMath, remarkGfmNoAutolink, remarkBreaks]}
                      rehypePlugins={[rehypeKatex, rehypeRaw, removePAroundKatex]}
                    />
                  </p>
                )}
              </div>
            </FocusLock>
          </div>,
          document.body
        )}
    </>
  );
}

export default React.memo(
  LatexHTMLRenderer,
  (pp, np) => pp.content === np.content && pp.className === np.className && pp.size === np.size
);
